/**
 * Button — 누르면 무언가 일어나는 것.
 *
 * Chip 과 같은 원칙: 기존 CSS 클래스를 그대로 소비하고 새 스타일링을 만들지 않는다.
 *
 * variant 는 두 갈래다 (문서: Button.mdx).
 *  - 범용 — 어느 화면에서나 쓴다. 무엇을 하는 버튼인지로 고른다
 *  - 컴포저 전용 — 그 자리에만 있는 모양. 다른 곳에 가져다 쓰지 않는다
 *
 * 스코프 주의 — default·primary·del 은 dcf.css(.dcf-root), 나머지는
 * chat.css(.chat-root) 에 정의되어 있어 해당 루트 밖에서는 스타일이 없다.
 */
import type { ButtonHTMLAttributes, ReactNode } from "react";

/** 범용 — 무엇을 하는 버튼인지로 고른다 */
export type ButtonRole =
  | "default"  // 보통 액션. 라벨이 있는 버튼의 기본값
  | "primary"  // 이 화면의 주 액션 — 화면당 하나
  | "icon"     // 라벨 없이 아이콘만. 툴바·패널 헤더 (36px 사각)
  | "del";     // 삭제 — 되돌릴 수 없는 것. 호버해야 빨강이 드러난다

/** 컴포저 전용 — 입력창의 그 자리에만 쓴다 (30px 원형) */
export type ComposerRole = "attach" | "ghost" | "send" | "stop";

export type ButtonVariant = ButtonRole | ComposerRole;

const CLASS: Record<ButtonVariant, string> = {
  // 범용
  default: "report-btn",          // 클래스명은 DCF 리포트 탭에서 유래 — 지금은 라벨 버튼 전반
  primary: "report-btn primary",
  icon: "icon-btn",
  del: "del-btn",
  // 컴포저 전용
  attach: "round-btn attach",     // 파일 첨부 — 면이 있는 원형
  ghost: "round-btn ghost",       // 빠른 실행(⚡) — 투명, 열리면 active
  send: "round-btn send",         // 전송 — .composer.has-text 아래에서 navy 로 살아난다
  stop: "round-btn stop",         // 생성 중단 — ink 채움. 멈춤은 눈에 띄어야 한다
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  /** 켜짐 상태 — icon·ghost 에 스타일이 있다 (.active) */
  active?: boolean;
  children: ReactNode;
}

/**
 * 앱의 모든 버튼. variant 가 기존 CSS 클래스로 1:1 매핑된다.
 *
 * 고르는 순서 — 라벨이 있나? → 이 화면의 주 액션인가? → 삭제인가?
 * 자세한 기준과 조합 규칙은 Button.mdx 를 본다.
 *
 * 어디에 쓰였나 — '화면에서 보기' 스토리가 실제 배치를 재현한다:
 * - 채팅 상단 툴바 (패널 토글·테마) — `icon` · ChatPage.tsx
 * - 컴포저 (첨부·빠른 실행·전송·중단) — `attach`·`ghost`·`send`·`stop` · Composer.tsx
 * - 우측 패널 헤더 (필터·검색·닫기) — `icon` · ResultsPanel.tsx, FilePreviewPanel.tsx
 * - DCF 워크스페이스 (재계산·저장·리포트) — `default`·`primary` · components/dcf/*
 * - DCF 표 행 삭제 — `del` · ProjectionTab.tsx, DcfDashboard.tsx
 */
export function Button({ variant = "default", active, className, children, ...rest }: ButtonProps) {
  const cls = [CLASS[variant], active && "active", className].filter(Boolean).join(" ");
  return (
    <button
      type="button"
      className={cls}
      aria-pressed={active === undefined ? undefined : active}
      {...rest}
    >
      {children}
    </button>
  );
}
